import type { Sort } from '@app/src/types/filter'
import { SortOrder } from '@app/src/types/filter'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { FacetOption, MrtColumnConfig } from './mrtCollectionTypes'
import type { MRT_VisibilityState } from 'material-react-table'
import { columnIdOfFilter, findFilterOnSamePath, multiStepPathOf } from './filters/multiStepFilter'
import { operatorNeedsValue, readStoredFilterValue } from './filters/filterOperator'

export type MrtColumnFilter = { id: string; value: unknown }
export type MrtSort = { id: string; desc: boolean }

// Turns raw facet items ({ value, label? }) into {value,label} options, dropping null values and
// applying an optional label formatter. Shared by the eager search-facet hook and the lazy per-column
// filter-options hook so the mapping lives in one place.
export const mapFacetItemsToOptions = (
  items: Array<{ value?: unknown; label?: unknown; count?: unknown }>,
  formatLabel?: (rawLabel: string) => string,
): FacetOption[] =>
  items
    .filter(item => item.value !== null && item.value !== undefined)
    .map(item => {
      const rawLabel = item.label ? String(item.label) : String(item.value)
      return {
        value: String(item.value),
        label: formatLabel ? formatLabel(rawLabel) : rawLabel,
        ...(typeof item.count === 'number' && { count: item.count }),
      }
    })

export const BASE_INCLUDES: string[] = []

export const deriveIncludes = <T>(
  visibleColumnIds: string[],
  columns: MrtColumnConfig<T>[],
  baseIncludes: string[] = BASE_INCLUDES,
): string[] => {
  const includes = new Set<string>(baseIncludes)
  visibleColumnIds.forEach(id => {
    columns.find(column => column.id === id)?.includes.forEach(include => includes.add(include))
  })
  return [...includes]
}

/** The columns the table itself renders - everything the filter bar sees, minus the filter-only entries. */
export const getRenderedColumns = <T>(columns: MrtColumnConfig<T>[]): MrtColumnConfig<T>[] =>
  columns.filter(column => !column.isFilterOnly)

export const isEmptyFilterValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return value.every(entry => entry === null || entry === undefined || entry === '')
  return false
}

// A filter is worth applying/keeping if it carries a value, or if its operator (empty/notEmpty) needs
// no value. Shared by the mapping below and the chip/add-filter commit logic.
export const isCommittableFilter = (stored: unknown): boolean => {
  const { operator, value } = readStoredFilterValue(stored)
  return !operatorNeedsValue(operator) || !isEmptyFilterValue(value)
}

/**
 * Sets a column's filter, in place among the chips when it already has one. Shared by the filter bar
 * and anything else that filters the table on the user's behalf, so a chip never appears twice.
 */
export const upsertColumnFilter = (
  columnFilters: MrtColumnFilter[],
  columnId: string,
  value: unknown,
): MrtColumnFilter[] =>
  columnFilters.some(filter => filter.id === columnId)
    ? columnFilters.map(filter => (filter.id === columnId ? { id: columnId, value } : filter))
    : [...columnFilters, { id: columnId, value }]

/** A chip edited onto the path of another chip of its column takes that chip's place, so no path has two chips. */
export const editColumnFilter = (
  columnFilters: MrtColumnFilter[],
  filterId: string,
  value: unknown,
): MrtColumnFilter[] => {
  const others = columnFilters.filter(filter => filter.id !== filterId)
  const replaced = findFilterOnSamePath(columnIdOfFilter(filterId), multiStepPathOf(value), others)
  return replaced ? upsertColumnFilter(others, replaced.id, value) : upsertColumnFilter(columnFilters, filterId, value)
}

export const mapColumnFiltersToFilterGroups = <T>(
  columnFilters: MrtColumnFilter[],
  columns: MrtColumnConfig<T>[],
): FilterGroup[] =>
  columnFilters.flatMap(({ id, value: stored }) => {
    const filter = columns.find(column => column.id === columnIdOfFilter(id))?.filter
    if (!filter || !isCommittableFilter(stored)) return []
    const { operator, value } = readStoredFilterValue(stored)
    return filter.toFilterGroups(value, operator)
  })

export const mapSorting = <T>(sorting: MrtSort[], columns: MrtColumnConfig<T>[]): Sort | undefined => {
  const [first] = sorting
  if (!first) return undefined
  const column = columns.find(entry => entry.id === first.id)
  // Sorting state is persisted separately from the columns, so it outlives them: a saved view, or a
  // column since hidden behind a flag or a permission. The backend resolves an unknown target by
  // reflection and throws, so send no sort at all rather than one nothing can answer.
  if (!column) return undefined
  return {
    target: column.sortField ?? first.id,
    order: first.desc ? SortOrder.DESCENDING : SortOrder.ASCENDING,
  }
}

/**
 * Applies each column's `isHiddenByDefault` to the persisted visibility state.
 *
 * Only for columns the state says nothing about: an explicit entry means the user has made a choice in
 * the column menu, and that always wins - including choosing to show a column that starts hidden.
 */
export const applyDefaultColumnVisibility = <T>(
  persistedVisibility: MRT_VisibilityState,
  columns: MrtColumnConfig<T>[],
): MRT_VisibilityState => {
  const unset = columns.filter(column => column.isHiddenByDefault && !Object.hasOwn(persistedVisibility, column.id))
  if (!unset.length) return persistedVisibility

  return { ...persistedVisibility, ...Object.fromEntries(unset.map(column => [column.id, false])) }
}
